import { TriangleAlert } from 'lucide-react';

import type {
  GapActionItems,
  GapCustomerFriction,
  GapDraftAgent,
  GapDraftView,
  GapEscalation,
  GapProjectOverview,
  GapRoiDerivation,
  GapRoiImpact,
  GapRunPrecedent,
  GapSeenBefore,
} from '@momentum/types';
import {
  GapCustomerFrictionLevel,
  GapRoiDerivationOutcome,
} from '@momentum/types';

import {
  ENRICHMENT_LABEL,
  OVERVIEW_LABEL,
  agentLabel,
  agentStatusTone,
} from '../lib/agents';
import { titleOf, titleOrigin } from '../lib/derive-title';
import { titleCase } from '../lib/format';
import {
  hasHumanEdit,
  resolveActionItems,
  resolveClaim,
  resolveCustomerFriction,
  resolveEscalation,
  resolveFullProjectContext,
  resolveLede,
  resolveMissability,
  resolvePotentialImpact,
  resolveRoiImpact,
  resolveSeenBefore,
  resolveSeverity,
} from '../lib/product-value';
import { EditedTag } from './EditedTag';
import { FromRunTag } from './FromRunTag';
import { InlineMarkdown } from './InlineMarkdown';
import { Placeholder } from './Placeholder';
import { Prose } from './Prose';
import { SectionRule as Rule } from './SectionRule';

/**
 * What the headline is, and who wrote it — the refiner, a reviewer, the finder,
 * or nobody, in which case the title on screen was derived from the claim.
 *
 * The finder's heading gets its own case rather than being folded into the
 * refiner's: both are agent output, but only one of them is the product layer,
 * and a reviewer reading "written by the refiner" over a run value would be
 * looking at enrichment that has not happened yet.
 */
function titleProvenance(draft: GapDraftView): { label: string; tone: string } {
  if (hasHumanEdit(draft, 'gap.title')) {
    return { label: 'gap.title · edited by a reviewer', tone: 'cyan' };
  }
  const origin = titleOrigin(draft);
  if (origin === 'gap') {
    return {
      label: `gap.title · written by ${OVERVIEW_LABEL}`,
      tone: 'emerald',
    };
  }
  if (origin === 'run') {
    return { label: 'source.title · written by the finder', tone: 'amber' };
  }
  return { label: 'derived from claim · no gap.title', tone: 'amber' };
}

/**
 * `severity` and `missability` are both 0–1 scores, and both are bracketed on
 * the bands the enrichment scores against: ≥0.8 high, ≥0.6 medium, below that
 * low. One function, because the bands are the same bands.
 */
function scoreBracket(score: number): { label: string; tone: string } {
  if (score >= 0.8) return { label: 'high bracket', tone: 'rose' };
  if (score >= 0.6) return { label: 'medium bracket', tone: 'amber' };
  return { label: 'low bracket', tone: '' };
}

/**
 * Why the enrichment scored the gap the way it did, in its own words. Used for
 * both scores: `severityReasoning` and `missabilityReasoning` are the same kind
 * of thing and read the same way.
 *
 * Not a two-zone field: it sits at the document root rather than in `gap`,
 * because a published `Gap` carries each score as the bare number the product
 * ranks on and the argument behind it stops at the draft. So there is no
 * `source` equivalent to fall back to and no resolver in `product-value.ts` —
 * the draft either has it or predates the enrichment writing one.
 *
 * It stays on screen after a reviewer overrides the score, relabelled. The
 * reasoning is the agent's, about the number the agent chose; a reviewer
 * disagreeing does not un-say it, and the disagreement is the thing worth
 * reading, which only works while both are visible.
 */
function ScoreReasoning({
  reasoning,
  isOverridden,
}: {
  reasoning: string | undefined;
  isOverridden: boolean;
}) {
  if (!reasoning) {
    return (
      <p className="gr-sev-why gr-sev-why-empty">
        No reasoning stored — the draft was scored before the enrichment wrote
        one.
      </p>
    );
  }
  return (
    <div className="gr-sev-why">
      <span className="gr-sev-why-k">
        Why {ENRICHMENT_LABEL} scored it
        {isOverridden && (
          <span className="gr-sev-why-stale">score since overridden</span>
        )}
      </span>
      <Prose text={reasoning} className="gr-sev-why-v" />
    </div>
  );
}

/**
 * One of the two 0–1 scores, its bracket, and the argument behind it. Severity
 * and missability render identically because they are the same shape on the
 * same scale — what differs is the question each answers, which the label
 * carries.
 */
function ScoreCard({
  label,
  score,
  isEdited,
  reasoning,
}: {
  label: string;
  score: number;
  isEdited: boolean;
  reasoning: string | undefined;
}) {
  const bracket = scoreBracket(score);
  return (
    <div className="gr-card">
      <span className="gr-card-k">
        {label}
        {isEdited && <EditedTag />}
      </span>
      <div className="gr-sev">
        <span className="gr-sev-num">{score.toFixed(2)}</span>
        <span className="gr-sev-track">
          <i className="gr-sev-fill" style={{ width: `${score * 100}%` }} />
        </span>
        <span className="gr-chip" data-tone={bracket.tone}>
          {bracket.label}
        </span>
      </div>
      <ScoreReasoning reasoning={reasoning} isOverridden={isEdited} />
    </div>
  );
}

/**
 * The friction ladder, weakest first, and what each rung is read as.
 *
 * The gloss is on screen because the level alone is not self-explaining: `LOW`
 * and `NONE` both sound like "cheap for the customer", and the line between them
 * — whether anything reaches them at all — is the one the enrichment actually
 * scores. `NONE` is emerald rather than blank: a fix that never touches the
 * customer is the best outcome on this axis, not the absence of a reading.
 */
const FRICTION_RUNGS: {
  level: GapCustomerFrictionLevel;
  gloss: string;
  tone: string;
}[] = [
  {
    level: GapCustomerFrictionLevel.NONE,
    gloss: 'nothing reaches them — the fix is ours end to end',
    tone: 'emerald',
  },
  {
    level: GapCustomerFrictionLevel.LOW,
    gloss: 'something reaches them; they act on none of it',
    tone: '',
  },
  {
    level: GapCustomerFrictionLevel.MEDIUM,
    gloss: 'they act — their own time, attention or decision',
    tone: 'amber',
  },
  {
    level: GapCustomerFrictionLevel.HIGH,
    gloss: 'it costs them beyond their own effort',
    tone: 'rose',
  },
];

/**
 * What the fix asks of the customer, and the argument for it.
 *
 * Beside the two 0–1 cards rather than inside one, because it is not a number:
 * the rungs are steps in what the customer has to DO, and the distance from
 * `LOW` to `MEDIUM` is not the distance from `MEDIUM` to `HIGH`. So the ladder
 * is drawn as four discrete segments, not a bar — a continuous fill would be
 * claiming a scale this axis does not have.
 */
function CustomerFrictionCard({
  friction,
  isEdited,
}: {
  friction: GapCustomerFriction;
  isEdited: boolean;
}) {
  const rungIndex = FRICTION_RUNGS.findIndex(
    (rung) => rung.level === friction.level
  );
  const rung = FRICTION_RUNGS[rungIndex];

  return (
    <div className="gr-card">
      <span className="gr-card-k">
        Customer friction · what the fix asks of the customer
        {isEdited && <EditedTag />}
      </span>
      <div className="gr-sev">
        <span className="gr-sev-num">{friction.level}</span>
        <span className="gr-fric-ladder" aria-hidden="true">
          {FRICTION_RUNGS.map((ladderRung, ladderIndex) => (
            <i
              key={ladderRung.level}
              data-filled={ladderIndex <= rungIndex}
              data-tone={rung?.tone}
            />
          ))}
        </span>
        {/* An unrecognised level is rendered rather than dropped: it means the
            enum moved under a stored gap, and a reviewer needs to see the word
            the draft holds, not a blank slot. */}
        <span className="gr-chip" data-tone={rung?.tone ?? 'amber'}>
          {rung?.gloss ?? 'level not on the current ladder'}
        </span>
      </div>
      <ScoreReasoning reasoning={friction.reasoning} isOverridden={isEdited} />
    </div>
  );
}

/**
 * The enriched lede: the account narrative and the project narrative, read one
 * after the other. Either can be blank on a draft, which is what the readiness
 * checklist reports — here a blank one just drops out of the pair.
 */
function ProjectOverviewLede({
  projectOverview,
}: {
  projectOverview: GapProjectOverview;
}) {
  const narrative = [projectOverview.account, projectOverview.project]
    .filter(Boolean)
    .join('\n\n');

  return <Prose text={narrative} />;
}

function EscalationTimeline({ escalation }: { escalation: GapEscalation }) {
  return (
    <ol className="gr-timeline">
      {escalation.events.map((escalationEvent, eventIndex) => (
        <li key={`${eventIndex}-${escalationEvent.title}`}>
          <time>
            {escalationEvent.dateLabel} · {escalationEvent.kind}
          </time>
          <p>
            <b>
              <InlineMarkdown text={escalationEvent.title} />
            </b>{' '}
            — <InlineMarkdown text={escalationEvent.description} />
            {escalationEvent.sources.map((source, sourceIndex) => (
              <span className="gr-src" key={`${sourceIndex}-${source.label}`}>
                {source.label}
              </span>
            ))}
          </p>
        </li>
      ))}
    </ol>
  );
}

function SeenBeforeProjects({ seenBefore }: { seenBefore: GapSeenBefore }) {
  return (
    <ol className="gr-timeline">
      {seenBefore.projects.map((project, projectIndex) => (
        <li key={project.id ?? `${projectIndex}-${project.name}`}>
          <time>
            <InlineMarkdown text={project.name} />
          </time>
          <p>
            <InlineMarkdown text={project.description} />
          </p>
        </li>
      ))}
    </ol>
  );
}

/**
 * The run's precedents standing in for an unwritten `seenBefore`.
 *
 * The finder writes more per precedent than the product shape carries — the
 * documented cost and why this deal is exposed to the same mechanism — and both
 * are the reason the precedent is worth citing, so neither is dropped.
 */
function PrecedentList({ precedents }: { precedents: GapRunPrecedent[] }) {
  return (
    <ol className="gr-timeline">
      {precedents.map((precedent) => (
        <li key={precedent.externalId}>
          <time>
            {precedent.dealName} · {precedent.outcome}
          </time>
          <p>
            {precedent.whatHappened}
            <br />
            Documented cost: {precedent.documentedCost}
            <br />
            {precedent.transferMechanism}
            <span className="gr-src">{precedent.externalId}</span>
          </p>
        </li>
      ))}
    </ol>
  );
}

function ActionItemsList({
  actionItems,
  isEdited,
}: {
  actionItems: GapActionItems;
  isEdited: boolean;
}) {
  return (
    <>
      <span className="gr-card-k">
        Proposed actions
        {isEdited && <EditedTag />}
      </span>
      {actionItems.intro !== undefined && (
        <Prose text={actionItems.intro} className="gr-prose" />
      )}
      <ul className="gr-actions-list">
        {actionItems.items.map((actionItem, itemIndex) => (
          <li key={`${itemIndex}-${actionItem.text.slice(0, 32)}`}>
            <span>{String(itemIndex + 1).padStart(2, '0')}</span>
            <span>
              <InlineMarkdown text={actionItem.text} />
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

function RoiImpactCard({
  roiImpact,
  isEdited,
}: {
  roiImpact: GapRoiImpact;
  isEdited: boolean;
}) {
  return (
    <div className="gr-card">
      <span className="gr-card-k">
        ROI impact
        {isEdited && <EditedTag />}
      </span>
      <div className="gr-card-v">
        <InlineMarkdown text={roiImpact.value} />
        {roiImpact.why !== undefined && <Prose text={roiImpact.why} />}
      </div>
    </div>
  );
}

/**
 * The ROI slot when the enrichment deliberately priced nothing.
 *
 * It occupies the card's place rather than the placeholder's, because on this
 * page the placeholder is a debt — a field somebody still owes — and a recorded
 * skip is a finished answer. The wording is the answer's: it says the figure
 * does not exist and points at the working below, which is the only place the
 * missing input is named.
 */
function RoiNotCalculatedCard() {
  return (
    <div className="gr-card gr-roinocalc">
      <span className="gr-card-k">ROI impact</span>
      <p className="gr-roinocalc-v">
        <TriangleAlert size={15} aria-hidden="true" />
        Could not calculate
      </p>
      <p className="gr-roinocalc-sub">
        No figure was written — see the derivation below for the input{' '}
        {ENRICHMENT_LABEL} could not establish.
      </p>
    </div>
  );
}

/**
 * How the enrichment reached the ROI figure — the developer's copy of the
 * argument whose reader-facing half is `roiImpact.why`.
 *
 * The two are deliberately not merged. `why` explains the number to the manager
 * who will act on it; this explains where every number in it was read, in
 * clause numbers, `externalId`s and document paths, and it is the only thing on
 * the page that can answer "is this figure trustworthy". Showing them together
 * is the point: the claim and its working, one under the other.
 *
 * It stays on the draft and never publishes, so — like `severityReasoning` —
 * there is no `source` fallback and no resolver in `product-value.ts`.
 *
 * A SKIPPED derivation is rendered exactly as fully as a CALCULATED one. It is
 * the case the field was added for: an agent that looked, could not price the
 * gap and said which input was missing is evidence about the prompt, where a
 * silently absent `roiImpact` is indistinguishable from an agent that never
 * tried.
 *
 * As fully, but not identically. A skip is announced rather than chipped: the
 * card is otherwise a wall of numbers and source paths, and a reader skimming
 * one reads "here is the arithmetic" long before reaching the reasoning that
 * says there was none. The banner, the heading and the inputs label all say the
 * same thing so no single glance can miss it — the numbers on a skipped
 * derivation are what the agent FOUND, never what it computed.
 */
function RoiDerivationCard({ derivation }: { derivation: GapRoiDerivation }) {
  const isCalculated =
    derivation.outcome === GapRoiDerivationOutcome.CALCULATED;

  return (
    <div className="gr-card gr-roideriv" data-outcome={derivation.outcome}>
      <span className="gr-card-k">
        {isCalculated
          ? `ROI derivation · how ${ENRICHMENT_LABEL} reached the figure`
          : `ROI derivation · why ${ENRICHMENT_LABEL} priced nothing`}
        <span className="gr-roideriv-scope">draft only · never published</span>
      </span>

      {isCalculated ? (
        <div className="gr-roideriv-head">
          <span className="gr-chip" data-tone="emerald">
            {derivation.outcome}
          </span>
          {derivation.formula !== undefined && (
            <code className="gr-roideriv-formula">{derivation.formula}</code>
          )}
        </div>
      ) : (
        <p className="gr-roideriv-skipped">
          <TriangleAlert size={15} aria-hidden="true" />
          <b>Could not calculate</b>
          <span>
            no ROI figure was written for this gap — the numbers below are what{' '}
            {ENRICHMENT_LABEL} found, not a calculation
          </span>
        </p>
      )}

      {derivation.inputs.length > 0 ? (
        <>
          {!isCalculated && (
            <span className="gr-roideriv-inputs-k">
              Found, but not enough to price it
            </span>
          )}
          <ul className="gr-roideriv-inputs">
            {derivation.inputs.map((input, inputIndex) => (
              <li key={`${inputIndex}-${input.label}`}>
                <span className="gr-roideriv-in-v">{input.value}</span>
                <span className="gr-roideriv-in-l">{input.label}</span>
                <span className="gr-src">{input.source}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="gr-roideriv-empty">
          {isCalculated
            ? 'No inputs recorded — the figure names no source, which is the one thing the derivation exists to rule out.'
            : 'No inputs found before the agent gave up.'}
        </p>
      )}

      <div className="gr-roideriv-why">
        <span className="gr-roideriv-why-k">
          {isCalculated ? 'Why this consequence is priced' : 'Why no figure'}
        </span>
        <Prose text={derivation.reasoning} className="gr-roideriv-why-v" />
      </div>
    </div>
  );
}

/**
 * The gap as a product reader would meet it, assembled from whatever exists.
 *
 * The section order is fixed and never collapses: a reviewer walking twenty
 * drafts with the keyboard needs the claim in the same place every time, so an
 * unwritten field becomes a placeholder rather than a missing section. Which
 * layer each value came from is stated inline — see `lib/product-value.ts`.
 */
export function ReaderDoc({ draft }: { draft: GapDraftView }) {
  const { source } = draft;

  const lede = resolveLede(draft);
  const escalation = resolveEscalation(draft);
  const claim = resolveClaim(draft);
  const potentialImpact = resolvePotentialImpact(draft);
  const roiImpact = resolveRoiImpact(draft);
  const actionItems = resolveActionItems(draft);
  const severity = resolveSeverity(draft);
  const missability = resolveMissability(draft);
  const customerFriction = resolveCustomerFriction(draft);
  const seenBefore = resolveSeenBefore(draft);
  const fullProjectContext = resolveFullProjectContext(draft);

  /* A skip is only the ROI story when there is no figure to tell a different
     one. A reviewer who typed a value over the agent's refusal has answered the
     question, and their number is what the slot shows. */
  const isRoiSkipped =
    draft.roiDerivation?.outcome === GapRoiDerivationOutcome.SKIPPED;

  const agentEntries = Object.entries(draft.agents) as [
    GapDraftAgent,
    NonNullable<GapDraftView['agents'][GapDraftAgent]>
  ][];

  return (
    <article className="gr-doc">
      <header className="gr-head">
        <h1 className="gr-h1">
          <InlineMarkdown text={titleOf(draft)} />
        </h1>
        <span className="gr-h1-tag" data-tone={titleProvenance(draft).tone}>
          {titleProvenance(draft).label}
        </span>
      </header>

      <div className="gr-stats">
        <div className="gr-stat">
          <span className="gr-stat-k">Confidence</span>
          <span className="gr-stat-v">{titleCase(source.confidence)}</span>
        </div>
        <div className="gr-stat">
          <span className="gr-stat-k">Project</span>
          <span className="gr-stat-v">{draft.scopeName}</span>
        </div>
        <div className="gr-stat">
          <span className="gr-stat-k">Type</span>
          <span className="gr-stat-v">{titleCase(source.claimType)}</span>
        </div>
      </div>

      <div className="gr-row-meta gr-row-meta-centred">
        <span className="gr-chip">{draft.variant}</span>
        <span className="gr-chip">
          {source.metadata.detector} · {source.claimType}
        </span>
        {source.isHypothesis && (
          <span className="gr-chip" data-tone="amber">
            hypothesis
          </span>
        )}
        <span className="gr-chip">run {source.metadata.runNumber}</span>
      </div>

      <Rule label="Overview" />
      <div className="gr-lede">
        {lede.from !== 'run' ? (
          <>
            <ProjectOverviewLede projectOverview={lede.projectOverview} />
            {lede.from === 'human' && <EditedTag />}
          </>
        ) : (
          <>
            <Prose text={lede.text} />
            <FromRunTag />
          </>
        )}
      </div>

      <Rule label="The gap" />
      <div className="gr-claim">
        <b>The gap:</b> <InlineMarkdown text={claim.text} />
        {claim.from === 'run' && <FromRunTag />}
        {claim.from === 'human' && <EditedTag />}
      </div>

      <Rule label="How it escalated" />
      {escalation.from !== 'missing' ? (
        <>
          <EscalationTimeline escalation={escalation.value} />
          {escalation.from === 'human' && <EditedTag />}
        </>
      ) : (
        <>
          <Placeholder
            text="Not written yet — an escalation timeline."
            owner={OVERVIEW_LABEL}
          />
          <div className="gr-drift">
            became flaggable {source.drift.date} — {source.drift.reasoning}
          </div>
        </>
      )}

      <Rule label="Impact & actions" />
      <div className="gr-grid2">
        <div>
          {potentialImpact.from !== 'missing' ? (
            <div className="gr-card">
              <span className="gr-card-k">
                Potential impact
                {potentialImpact.from === 'human' && <EditedTag />}
              </span>
              <div className="gr-card-v">
                <Prose text={potentialImpact.value} />
              </div>
            </div>
          ) : (
            <Placeholder
              text="Potential impact analysis missing."
              owner={ENRICHMENT_LABEL}
            />
          )}
        </div>
        <div>
          {roiImpact.from !== 'missing' ? (
            <RoiImpactCard
              roiImpact={roiImpact.value}
              isEdited={roiImpact.from === 'human'}
            />
          ) : isRoiSkipped ? (
            /* Not a hole. A hole means nobody has written this yet, and the
               reviewer's next move is to chase the agent that owes it; a
               recorded skip means the agent looked, could not price the gap and
               said why, and the next move is to read that reasoning. Drawing
               the two the same way is what sent readers to the derivation card
               to work out which one they were looking at. */
            <RoiNotCalculatedCard />
          ) : (
            <Placeholder text="ROI impact calculation missing." />
          )}
        </div>
      </div>

      {/* Full width under the card rather than inside its column: the sources
          are document paths and record ids, and half of a two-column grid
          wraps them into something nobody can read back to Mongo. */}
      <div className="gr-stack-top">
        {draft.roiDerivation ? (
          <RoiDerivationCard derivation={draft.roiDerivation} />
        ) : (
          roiImpact.from !== 'missing' && (
            <p className="gr-roideriv-absent">
              No derivation stored — the figure above was written before{' '}
              {ENRICHMENT_LABEL} recorded its working.
            </p>
          )
        )}
      </div>

      <div className="gr-stack-top">
        {actionItems.from !== 'missing' ? (
          <ActionItemsList
            actionItems={actionItems.value}
            isEdited={actionItems.from === 'human'}
          />
        ) : (
          <Placeholder
            text="Proposed mitigations not generated yet."
            owner={ENRICHMENT_LABEL}
          />
        )}
      </div>

      <div className="gr-stack-top">
        {severity.from !== 'missing' ? (
          <ScoreCard
            label="Severity · 0–1 business impact"
            score={severity.value}
            isEdited={severity.from === 'human'}
            reasoning={draft.severityReasoning}
          />
        ) : (
          <Placeholder
            text="Severity unassigned — a 0–1 business-impact score, never source.impact."
            owner={ENRICHMENT_LABEL}
          />
        )}
      </div>

      <div className="gr-stack-top">
        {missability.from !== 'missing' ? (
          <ScoreCard
            label="Missability · 0–1 chance it goes unnoticed"
            score={missability.value}
            isEdited={missability.from === 'human'}
            reasoning={draft.missabilityReasoning}
          />
        ) : (
          <Placeholder
            text="Missability unassigned — a 0–1 chance the gap goes unnoticed, never source.missability."
            owner={ENRICHMENT_LABEL}
          />
        )}
      </div>

      <div className="gr-stack-top">
        {customerFriction.from !== 'missing' ? (
          <CustomerFrictionCard
            friction={customerFriction.value}
            isEdited={customerFriction.from === 'human'}
          />
        ) : (
          <Placeholder
            text="Customer friction unscored — what the proposed fix asks of the customer, never source.customerFriction."
            owner={ENRICHMENT_LABEL}
          />
        )}
      </div>

      <Rule label="Seen before" />
      {(seenBefore.from === 'gap' || seenBefore.from === 'human') && (
        <>
          <SeenBeforeProjects seenBefore={seenBefore.seenBefore} />
          {seenBefore.from === 'human' && <EditedTag />}
        </>
      )}
      {seenBefore.from === 'run' && (
        <>
          <PrecedentList precedents={seenBefore.precedents} />
          <FromRunTag />
        </>
      )}
      {seenBefore.from === 'run-note' && (
        <div className="gr-prose gr-prose-quiet">
          <Prose text={seenBefore.note} />
          <FromRunTag />
        </div>
      )}
      {seenBefore.from === 'missing' && (
        <Placeholder text="No precedent search has run for this draft." />
      )}

      <Rule label="Full project context" />
      <details className="gr-fold">
        <summary>
          Full project context payload{' '}
          {fullProjectContext.from === 'run' && <FromRunTag />}
          {fullProjectContext.from === 'human' && <EditedTag />}
        </summary>
        {/* `gr-fold-body` is `pre-wrap`, so the payload keeps its own line
            breaks; only the inline tokens inside them are rendered. */}
        <div className="gr-fold-body">
          <InlineMarkdown text={fullProjectContext.text} />
        </div>
      </details>

      <Rule label="Agents" />
      {agentEntries.length === 0 ? (
        <div className="gr-empty">
          <b>Agents</b>No enrichment agent has run on this draft.
        </div>
      ) : (
        <>
          <div className="gr-agents">
            {agentEntries.map(([agent, agentEntry]) => (
              <span className="gr-agentchip" key={agent}>
                <i
                  className="gr-dot-s"
                  data-tone={agentStatusTone(agentEntry.status)}
                />
                <b>{agentLabel(agent)}</b> {agentEntry.status}
              </span>
            ))}
          </div>
          {agentEntries
            .filter(([, agentEntry]) => agentEntry.error !== undefined)
            .map(([agent, agentEntry]) => (
              <div className="gr-note gr-stack-top" key={agent}>
                <TriangleAlert />
                <div>
                  <strong>{agentLabel(agent)} failed.</strong>{' '}
                  {agentEntry.error}
                </div>
              </div>
            ))}
        </>
      )}
    </article>
  );
}
